import useEmblaCarousel, { type UseEmblaCarouselType as CarouselApi } from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Children, forwardRef, useCallback, useEffect, useState } from 'react';

import { cn } from '../utils';
import { Button } from './button';

type CarouselProps = {
  opts?: Parameters<typeof useEmblaCarousel>[0];
  plugins?: Parameters<typeof useEmblaCarousel>[1];
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: NonNullable<ReturnType<typeof useEmblaCarousel>[1]>) => void;
};

const Carousel = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & CarouselProps>(
  ({ orientation = 'horizontal', opts, setApi, plugins, className, children, ...props }, ref) => {
    const [emblaRef, emblaApi] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins
    );

    const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
    const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const onSelect = useCallback(() => {
      if (!emblaApi) return;

      setSelectedIndex(emblaApi.selectedScrollSnap());
      setPrevBtnEnabled(emblaApi.canScrollPrev());
      setNextBtnEnabled(emblaApi.canScrollNext());
    }, [emblaApi]);

    useEffect(() => {
      if (!emblaApi) return;

      onSelect();
      emblaApi.on('select', onSelect);
      emblaApi.on('reInit', onSelect);

      if (setApi) {
        setApi(emblaApi);
      }
    }, [emblaApi, onSelect, setApi]);

    return (
      <div ref={ref} className={cn('relative', className)} {...props}>
        <div ref={emblaRef} className="overflow-hidden" dir="ltr">
          <div
            className={cn('flex', orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col', [
              orientation === 'horizontal' ? 'touch-pan-x' : 'touch-pan-y',
            ])}
          >
            {Children.map(children, (child) => (
              <div
                className={cn(
                  'min-w-0 flex-[0_0_100%]',
                  orientation === 'horizontal' ? 'pl-4' : 'pt-4'
                )}
              >
                {child}
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 py-2">
          <Button
            variant="outline"
            size="icon"
            className={cn('h-8 w-8 rounded-full', !prevBtnEnabled && 'opacity-50')}
            disabled={!prevBtnEnabled}
            onClick={() => emblaApi?.scrollPrev()}
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">Previous slide</span>
          </Button>
          <div className="flex gap-1">
            {Children.map(children, (_, index) => (
              <Button
                key={index}
                variant="outline"
                size="icon"
                className={cn(
                  'h-2 w-2 rounded-full',
                  selectedIndex === index ? 'bg-primary' : 'bg-primary'
                )}
                onClick={() => emblaApi?.scrollTo(index)}
              >
                <span className="sr-only">Jump to slide {index + 1}</span>
              </Button>
            ))}
          </div>
          <Button
            variant="outline"
            size="icon"
            className={cn('h-8 w-8 rounded-full', !nextBtnEnabled && 'opacity-50')}
            disabled={!nextBtnEnabled}
            onClick={() => emblaApi?.scrollNext()}
          >
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">Next slide</span>
          </Button>
        </div>
      </div>
    );
  }
);
Carousel.displayName = 'Carousel';

const CarouselContent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} role="list" className={cn('', className)} {...props} />
  )
);
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} role="listitem" className={cn('', className)} {...props} />
  )
);
CarouselItem.displayName = 'CarouselItem';

const CarouselPrevious = forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
  ({ className, ...props }, ref) => (
    <Button
      ref={ref}
      variant="outline"
      size="icon"
      className={cn('absolute left-2 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full', className)}
      {...props}
    >
      <ChevronLeft className="h-4 w-4" />
      <span className="sr-only">Previous slide</span>
    </Button>
  )
);
CarouselPrevious.displayName = 'CarouselPrevious';

const CarouselNext = forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
  ({ className, ...props }, ref) => (
    <Button
      ref={ref}
      variant="outline"
      size="icon"
      className={cn('absolute right-2 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full', className)}
      {...props}
    >
      <ChevronRight className="h-4 w-4" />
      <span className="sr-only">Next slide</span>
    </Button>
  )
);
CarouselNext.displayName = 'CarouselNext';

export {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
};
